import { apiFetch } from './api.js';
import type { CreditSummary } from '../session/types.js';

export interface CreditPack {
  key: string;
  name: string;
  /** How many site builds this pack is. The pack's real definition (D-9b). */
  builds: number;
  /** What that comes to at the live build cost. */
  credits: number;
  amountMinor: number;
  /** What one build works out at, from the server. */
  perBuildAmountMinor: number;
  /** Rack less charged: the green savings line. Zero on the rack-rate pack. */
  savingMinor: number;
  /** Whole percent off rack, rounded down. Zero on the rack-rate pack. */
  discountPercent: number;
  currency: string;
  rackRateMinor: number;
  featured?: boolean;
}

/**
 * What the things credits buy cost, today — D-9 ruling 6.
 *
 * Served rather than known, because an operator can change either of these
 * without a deploy. Every phrase on the buy page that translates credits into
 * builds or years comes from here.
 */
export interface OrderCostSummary {
  siteBuildCredits: number;
  yearExtensionCredits: number;
}

export interface LedgerEntry {
  id: string;
  entryType: string;
  amount: number;
  reason: string | null;
  /** Who is answerable, in words. "System (payment)" for a webhook purchase. */
  createdByLabel: string;
  createdAt: string;
}

export interface LedgerResponse {
  entries: LedgerEntry[];
  pagination: { limit: number; offset: number; total: number };
  credits: CreditSummary;
}

export type PaymentStatus = 'initiated' | 'settled' | 'credited' | 'failed';

export interface PaymentRecord {
  id: string;
  status: PaymentStatus;
  /** The goods price, ex-VAT (D-13). Not what the card was charged. */
  amountMinor: number;
  /** Tax added at checkout. 0 where none applied. */
  taxMinor: number;
  /** What the card was charged: goods plus tax, added up by the server. */
  totalMinor: number;
  currency: string;
  creditsPurchased: number;
  packKey: string | null;
  receiptUrl: string | null;
  failureReason: string | null;
  /** True when an operator credited this by hand rather than a webhook. */
  creditedManually: boolean;
  /**
   * What has come back out of this charge - D-14 Item 5.
   *
   * **`status` is deliberately not extended to say so.** `refundState` is
   * derived by the server from `refundedMinor` against goods plus tax, so the
   * browser never decides what "fully refunded" means and a fifth
   * `PaymentStatus` never had to be agreed on by the state machine, the sales
   * filters and every existing test.
   */
  refundedMinor: number;
  refundState: 'none' | 'partial' | 'full';
  refundRef: string | null;
  refundedAt: string | null;
  disputedAt: string | null;
  disputeRef: string | null;
  disputeOutcome: 'won' | 'lost' | 'reinstated' | null;
  createdAt: string;
  creditedAt: string | null;
}

/** A volume tier, as the API describes it. Never re-derived in the client. */
export interface RateBand {
  minCredits: number;
  /** The same rung in builds, which is how the ladder is actually set (D-9b). */
  minBuilds: number | null;
  ratePerCreditMinor: number;
  discountPercent: number;
}

export interface CreditPacksResponse {
  packs: CreditPack[];
  /** Purchase terms, served by the API so there is one source of truth. */
  disclosure: string[];
  /**
   * What the prices on this page do not include - D-13 ruling, item 1c.
   *
   * Served rather than written here, because the payment page shows the same
   * sentence and a second copy is a second thing to keep true.
   */
  taxNotice: string;
  /** The tier table (D-8). The page shows these rates; it never computes them. */
  bands: RateBand[];
  /** Order size limits, served so the page cannot drift from the engine. */
  bounds: { minCredits: number; maxCredits: number };
  rackRateMinor: number;
  currency: string;
  /** What a build and a year cost, so the page's copy can be derived. */
  orderCosts: OrderCostSummary;
}

/** The next tier up, and what it would take to reach it. */
export interface NextBand {
  minCredits: number;
  addCredits: number;
  ratePerCreditMinor: number;
  discountPercent: number;
}

/**
 * A priced order.
 *
 * Every figure here comes from the server. The buy page displays this and
 * calculates nothing itself — the pricing rules have one home, and it is not
 * the browser.
 */
/** A code that applied. */
export interface AppliedCoupon {
  code: string;
  percentOff: number;
  /** What it took off the tier price. 0 when the volume rate still won. */
  discountMinor: number;
  /** True when the coupon price beat the volume rate and set the charge. */
  won: boolean;
}

/** A code that did not apply, and why. The order is still buyable. */
export interface RejectedCoupon {
  code: string;
  reason: string;
  message: string;
}

export interface CreditOrderQuote {
  /** What the buyer asked for, before any lift. */
  requestedCredits: number;
  /** What the order actually grants — more than requested when lifted. */
  credits: number;
  lifted: boolean;
  rateBand: RateBand;
  rackAmountMinor: number;
  listAmountMinor: number;
  chargeAmountMinor: number;
  savingMinor: number;
  effectiveRateMinor: number;
  nextBand: NextBand | null;
  currency: string;
  coupon: AppliedCoupon | null;
  couponRejected: RejectedCoupon | null;
  /**
   * Whole site builds these credits cover, rounded down — the summary box's
   * "up to 6 site builds". From the server, at today's build cost.
   */
  siteBuildsCovered: number;
  /** How many site renewals these credits cover, rounded down. */
  renewalsCovered: number;
}

/** What the buyer has assembled. */
export interface CreditOrderRequest {
  presetQuantities?: Record<string, number>;
  customCredits?: number;
  couponCode?: string;
}

export function fetchCreditPacks(): Promise<CreditPacksResponse> {
  return apiFetch<CreditPacksResponse>('/api/credit-packs');
}

export function fetchLedger(agencyId: string): Promise<LedgerResponse> {
  return apiFetch<LedgerResponse>(`/api/agencies/${agencyId}/ledger`);
}

export function fetchPayments(): Promise<{ payments: PaymentRecord[]; credits: CreditSummary }> {
  return apiFetch<{ payments: PaymentRecord[]; credits: CreditSummary }>('/api/payments');
}

/** Prices an assembled order. Called on every change to the builder. */
export function quoteCreditOrder(order: CreditOrderRequest): Promise<{ quote: CreditOrderQuote }> {
  return apiFetch<{ quote: CreditOrderQuote }>('/api/credit-orders/quote', {
    method: 'POST',
    body: order,
  });
}

/**
 * Starts checkout for an assembled order.
 *
 * The quantities go up, never the price: the server recomputes the charge from
 * the same engine that produced the quote, so there is nothing here for a
 * tampered request to change.
 */
export function startCheckout(order: CreditOrderRequest): Promise<{ redirectUrl: string }> {
  return apiFetch<{ redirectUrl: string }>('/api/checkout/sessions', {
    method: 'POST',
    body: order,
  });
}

/**
 * Starts checkout for a pack bought by key.
 *
 * Only the unlisted `pilot` pack still needs this, and it goes away with the
 * pack in CO-Phase 3.
 */
export function startPackCheckout(packKey: string): Promise<{ redirectUrl: string }> {
  return apiFetch<{ redirectUrl: string }>('/api/checkout/sessions', {
    method: 'POST',
    body: { packKey },
  });
}
